import { useCallback, useState } from 'react';
import { Column } from '@elementary/common-ui/components';
import * as atoms from '@elementary/common-ui/atoms';
import {
  AgentCloudTestConfigChange,
  AgentCloudTestConfigFields,
  AgentTestConfigPlan,
  AgentCloudTestCreateChange,
  AgentTestOperation,
  getAgentTestChangeRowId,
  getUnmappedConfigFieldEntries,
  humanizeConfigFieldKey,
  isAgentCloudTestCreateChange,
} from '@elementary/common-ui/types';
import useTableAssets, {
  TableAssetsData,
} from '@elementary/common-ui/hooks/useTableAssets';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import useGraphqlRequestParams from '@elementary/common-ui/hooks/useGraphqlRequestParams';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { TEST_CONFIGURATION } from '@elementary/common-ui/constants/queryKeys';
import { useRecoilValue } from 'recoil';
import { useQueryClient } from '@tanstack/react-query';
import { request } from 'graphql-request';
import { CloudAnomalyTestConfig } from 'shared/types';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import { getTestConfiguration } from 'shared/components/TestOverviewSidePanel/services/api';
import { buildCloudPlanWizardState } from 'testManagement/utils/cloudPlanWizardMapping';
import { AgentConfigPreviewShell } from '../agentConfigPreviewShared';
import {
  BREADCRUMB_LABELS,
  COLUMN_SCHEMA,
  KNOWN_CONFIG_FIELD_KEYS,
  STORAGE_KEY,
  TEST_TYPE_FIELDS,
  TEST_TYPE_NAMES,
} from './constants';
import { ConfigFieldKey, ColumnSchema, PreviewRow } from './types';

const planSeverityToRowSeverity = (severity?: string | null): string => {
  if (!severity) return 'warn';
  return severity.toLowerCase() === 'error' ? 'error' : 'warn';
};

const formatBaselineColumnLabels = (
  columns: { column_name: string }[] | null | undefined,
): string[] =>
  (columns ?? [])
    .map((column) => column.column_name?.trim())
    .filter((name): name is string => !!name);

const buildCreatePreviewRow = (
  change: AgentCloudTestCreateChange,
  asset?: TableAssetsData,
): PreviewRow => ({
  id: getAgentTestChangeRowId(change),
  asset_id: change.assetId,
  schema: asset?.schemaName || '',
  table: asset?.tableName || asset?.name || '',
  package: 'elementary',
  name: TEST_TYPE_NAMES[change.testType] ?? change.testType,
  type: TEST_TYPE_NAMES[change.testType] ?? change.testType,
  severity: planSeverityToRowSeverity(change.severity?.new),
  owners: asset?.owners || [],
  tags: asset?.tags || [],
  args: {},
  updated_at: '',
  meta: {},
  is_singular: false,
  is_custom_sql: false,
  original_path: '',
  is_cloud: true,
  channels: [],
  model_name: asset?.name || '',
  database_name: asset?.dbName || '',
  materialization: asset?.materialization,
  configChange: change,
  isCreateRow: true,
});

const useCloudCreateRowData = (isCreateOperation: boolean) => {
  const { data: tableAssetsMap, isFetching } = useTableAssets({
    enabled: isCreateOperation,
  });

  const buildCreatePreviewRowForPlan = useCallback(
    (change: AgentCloudTestCreateChange): PreviewRow =>
      buildCreatePreviewRow(change, tableAssetsMap?.get(change.assetId)),
    [tableAssetsMap],
  );

  return {
    buildCreatePreviewRow: buildCreatePreviewRowForPlan,
    isLoading: isFetching,
  };
};

const getPresentFields = (plan: AgentTestConfigPlan): Set<string> => {
  const fields = new Set<string>();
  if (plan.operation === 'delete') return fields;

  for (const change of plan.changes) {
    const allowedKeys = isAgentCloudTestCreateChange(change)
      ? new Set(TEST_TYPE_FIELDS[change.testType])
      : null;

    for (const { key } of COLUMN_SCHEMA) {
      if (allowedKeys && !allowedKeys.has(key)) continue;
      if (change[key]) {
        fields.add(key);
      }
    }

    // Feeds tableKey so leftover-key changes remount the table; unmapped columns are built separately.
    for (const [key] of getUnmappedConfigFieldEntries(
      change,
      KNOWN_CONFIG_FIELD_KEYS,
    )) {
      fields.add(`unmapped:${key}`);
    }
  }
  return fields;
};

const getUnmappedKeysFromPlan = (plan: AgentTestConfigPlan): string[] => {
  const keys = new Set<string>();
  for (const change of plan.changes) {
    for (const [key] of getUnmappedConfigFieldEntries(
      change,
      KNOWN_CONFIG_FIELD_KEYS,
    )) {
      keys.add(key);
    }
  }
  return Array.from(keys).sort();
};

const getSubmitLabel = (
  operation: AgentTestOperation,
  selectedCount: number,
): string => {
  if (operation === 'create') {
    return `Create ${pluralizeString(selectedCount, 'test', 'tests')}`;
  }
  if (operation === 'delete') {
    return `Delete ${pluralizeString(selectedCount, 'test', 'tests')}`;
  }
  return 'Approve';
};

const CloudTestAgentConfigPreview = () => {
  const navigate = useNavigateWithEnv();
  const envId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { accessToken, gqlEndpoint } = useGraphqlRequestParams({});
  const { openSnackbar } = useSnackbar();
  const plan = useRecoilValue(atoms.agentCloudTestConfigPlan);
  const isCreateOperation = plan?.operation === 'create';
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const { buildCreatePreviewRow: buildRow, isLoading: isCreateRowDataLoading } =
    useCloudCreateRowData(isCreateOperation);

  const handleRowClick = useCallback(
    async (row: PreviewRow) => {
      if (!plan || plan.operation === 'delete') return;

      const change = row.configChange;
      const isCreate = isAgentCloudTestCreateChange(change);
      const assetId = isCreate ? change.assetId : row.asset_id;
      if (!assetId) return;

      // On update, seed the wizard from the test's live config so untouched
      // fields show real values; the agent's changes are overlaid on top.
      let currentConfig: CloudAnomalyTestConfig | undefined;
      const testId = isCreate ? undefined : change.testId;
      if (plan.operation === 'update' && testId) {
        setIsLoadingConfig(true);
        try {
          const data = await queryClient.fetchQuery<{
            test?: { config?: CloudAnomalyTestConfig };
          }>({
            queryKey: [TEST_CONFIGURATION, testId, envId],
            queryFn: () =>
              request(
                gqlEndpoint,
                getTestConfiguration,
                { id: testId },
                { authorization: `Bearer ${accessToken}` },
              ),
          });
          currentConfig = data?.test?.config;
        } catch {
          openSnackbar({
            message:
              'Could not load the current test configuration; showing defaults.',
            variant: 'error',
          });
        } finally {
          setIsLoadingConfig(false);
        }
      }

      const state = buildCloudPlanWizardState({
        change,
        operation: plan.operation as 'create' | 'update',
        assetId,
        testName: row.name,
        currentConfig,
      });
      if (!state) return;

      navigate('/test-management/plan-edit-test', { state });
    },
    [
      navigate,
      plan,
      queryClient,
      gqlEndpoint,
      accessToken,
      envId,
      openSnackbar,
    ],
  );

  const buildDynamicColumns = useCallback(
    ({
      plan: currentPlan,
      presentFields,
      columnBuilders,
    }: Parameters<
      React.ComponentProps<
        typeof AgentConfigPreviewShell<AgentCloudTestConfigChange, PreviewRow>
      >['buildDynamicColumns']
    >[0]) => {
      if (currentPlan.operation === 'delete') return [];

      const getField =
        <K extends ConfigFieldKey>(key: K) =>
        (row: PreviewRow): AgentCloudTestConfigFields[K] | undefined =>
          row.configChange[key];

      const buildColumnForSchema = (
        schema: ColumnSchema,
      ): Column<PreviewRow> => {
        switch (schema.type) {
          case 'dropdown':
            return columnBuilders.buildDropdownColumn(
              schema.id,
              schema.header,
              schema.options,
              getField(schema.key),
            );
          case 'period':
            return columnBuilders.buildPeriodColumn(
              schema.id,
              schema.header,
              getField(schema.key),
            );
          case 'number':
            return columnBuilders.buildNumberColumn(
              schema.id,
              schema.header,
              getField(schema.key),
              schema.suffix,
            );
          case 'boolean':
            return columnBuilders.buildBooleanColumn(
              schema.id,
              schema.header,
              schema.trueLabel,
              schema.falseLabel,
              getField(schema.key),
            );
          case 'list':
            return columnBuilders.buildListColumn(
              schema.id,
              schema.header,
              schema.itemLabel,
              getField(schema.key),
              { isElasticLayout: true },
            );
          case 'baselineColumns':
            return columnBuilders.buildListColumn(
              schema.id,
              schema.header,
              ['column', 'columns'],
              (row) => {
                const field = row.configChange.baseline;
                if (!field) return undefined;
                return {
                  current: formatBaselineColumnLabels(field.current),
                  new: formatBaselineColumnLabels(field.new),
                };
              },
              { isElasticLayout: true },
            );
          case 'timeRanges':
            return columnBuilders.buildGenericChangeColumn(
              schema.id,
              schema.header,
              getField(schema.key),
            );
        }
      };

      const knownColumns = COLUMN_SCHEMA.filter((schema) =>
        presentFields.has(schema.key),
      ).map(buildColumnForSchema);

      const unmappedColumns = getUnmappedKeysFromPlan(currentPlan).map((key) =>
        columnBuilders.buildGenericChangeColumn(
          `unmapped_${key}_change`,
          humanizeConfigFieldKey(key),
          (row) => {
            const entries = getUnmappedConfigFieldEntries(
              row.configChange,
              KNOWN_CONFIG_FIELD_KEYS,
            );
            return entries.find(([entryKey]) => entryKey === key)?.[1];
          },
        ),
      );

      return [...knownColumns, ...unmappedColumns];
    },
    [],
  );

  return (
    <AgentConfigPreviewShell
      planAtom={atoms.agentCloudTestConfigPlan}
      isCreateChange={isAgentCloudTestCreateChange}
      buildCreatePreviewRow={(change) => {
        if (!isAgentCloudTestCreateChange(change)) {
          throw new Error('Expected create change');
        }
        return buildRow(change);
      }}
      isCreateRowDataLoading={isCreateRowDataLoading}
      getPresentFields={getPresentFields}
      buildDynamicColumns={buildDynamicColumns}
      getSubmitLabel={getSubmitLabel}
      breadcrumbLabels={BREADCRUMB_LABELS}
      storageKey={STORAGE_KEY}
      onRowClick={isLoadingConfig ? undefined : handleRowClick}
    />
  );
};

export default CloudTestAgentConfigPreview;
